import { useState, useEffect, useCallback, useRef } from "react";
import "../ts/typings/NFC";



export function useNFC() {
	const [nfcSupported, setNfcSupported] = useState<boolean>(false);
	const [isReading, setIsReading] = useState<boolean>(false);
	const [data, setData] = useState<unknown>(null); // Can be string or object
	const [error, setError] = useState<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	useEffect(() => {
		setNfcSupported(typeof window !== "undefined" && "NDEFReader" in window);
	}, []);

	const stopNFCReading = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
		setIsReading(false);
	}, []);

	const startNFCReading = useCallback(async () => {
		setError(null);
		setData(null);

		if (!nfcSupported) {
			setError("Web NFC is not supported here.");
			return;
		}

		try {
			if (isReading) return;

			const ndef = new window.NDEFReader();
			abortControllerRef.current = new AbortController();
			await ndef.scan({ signal: abortControllerRef.current.signal });
			setIsReading(true);

			ndef.onreading = (event: NDEFReadingEvent) => {
				const decoder = new TextDecoder();

				for (const record of event.message.records) {
					//TODO Check record.recordType
					if (record.data) {
						const decodedText = decoder.decode(record.data);

						// --- Logic for JSON vs String ---
						try {
							const json = JSON.parse(decodedText) as unknown;
							setData(json); // It's an object!
						} catch {
							setData(decodedText); // It's just a regular string
						}
					}
				}

				stopNFCReading(); // stop after first read
			};

			ndef.onreadingerror = () => setError("Tag detected but cannot be read.");
		} catch (err) {
			if ((err as Error).name !== "AbortError") {
				setError(`Error: ${(err as Error).message}`);
			}
			setIsReading(false);
			abortControllerRef.current = null;
		}
	}, [nfcSupported, stopNFCReading]);

	useEffect(() => {
		return () => stopNFCReading();
	}, [stopNFCReading]);

	return { nfcSupported, isReading, startNFCReading, stopNFCReading, data, error };
}